import { useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader/Loader.jsx";
import Button from "../../components/Button/Button.jsx";
import FormField from "../../components/FormField/FormField.jsx";
import Box from "../../components/Box/Box.jsx";
import { Eye, EyeOff, Check, X } from "lucide-react";

import { toast } from "react-toastify";
import styles from "../ActivateAccount/ActivateAccount.module.scss";
import activateAccount from "../../services/login/activate-account.js";

// ---------------------------------------------------------
// VALIDACIONES
// ---------------------------------------------------------
const validatePasswordRules = (pwd) => ({
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=/\\[\]]/.test(pwd),
});

export default function ActivateAccount() {
    let { hash } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [repeatPassword, setRepeatPassword] = useState("");

    // MOSTRAR / OCULTAR PARA AMBOS CAMPOS
    const [showPwd, setShowPwd] = useState(false);
    const [showRepeatPwd, setShowRepeatPwd] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    if (!hash) {
        return <Navigate to='/login' replace state={{ from: location }} />;
    }

    const rules = validatePasswordRules(password);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (password !== repeatPassword) {
            toast.error("Las contraseñas no coinciden.");
            return;
        }

        if (Object.values(rules).includes(false)) {
            toast.error("La contraseña no cumple con los requisitos.");
            return;
        }

        setSubmitting(true);

        try {
            const params = { password, repeatPassword, hash };
            const response = await activateAccount(params);

            // Si la respuesta tiene estado 0, es un error
            if (response.data?.estado === 0) {
                const mensaje = response.data?.mensaje || "No se pudo activar tu cuenta.";
                
                // Si hay detalles (array de errores de validación), mostrarlos
                if (response.data?.detalles && Array.isArray(response.data.detalles)) {
                    toast.error(
                        <div>
                            <strong>{mensaje}</strong>
                            <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                                {response.data.detalles.map((error, idx) => (
                                    <li key={idx}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    );
                } else {
                    toast.error(mensaje);
                }
                return;
            }

            // Si estado es 1, es éxito
            if (response.data?.estado === 1) {
                toast.success(response.data?.mensaje || "¡Cuenta activada con éxito!");
                setTimeout(() => navigate('/login', { replace: true }), 1500);
            }

        } catch (err) {
            const msg =
                err?.response?.data?.mensaje ||
                err?.response?.data?.description ||
                err?.message ||
                "No se pudo activar la cuenta.";

            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`${styles.loginContainer} bgOrangeGradient`}>
            <div className={styles.loginCard}>
                <Box columns={1} padding={5} gap={5}>
                    <h3 className={styles.loginTitle}>Genera tu contraseña</h3>

                    <form onSubmit={handleFormSubmit} className={styles.loginForm}>
                        
                        {/* ------------------------ */}
                        {/*    CONTRASEÑA NUEVA     */}
                        {/* ------------------------ */}
                        <div style={{ position: "relative" }}>
                            <FormField
                                label="Contraseña"
                                name="password"
                                type={showPwd ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="Ingresa tu contraseña"
                                disabled={submitting}
                            />

                            <div
                                onClick={() => setShowPwd(!showPwd)}
                                style={{
                                    position: "absolute",
                                    right: 10,
                                    top: 38,
                                    cursor: "pointer",
                                    color: "#666"
                                }}
                            >
                                {showPwd ? <EyeOff size={20} /> : <Eye size={20} />}
                            </div>
                        </div>

                        {/* CHECKLIST */}
                        {password.length > 0 && (
                            <div style={{ fontSize: 13, marginBottom: 10 }}>
                                <CheckItem ok={rules.length} text="Mínimo 8 caracteres" />
                                <CheckItem ok={rules.upper} text="Una letra mayúscula" />
                                <CheckItem ok={rules.lower} text="Una letra minúscula" />
                                <CheckItem ok={rules.number} text="Un número" />
                                <CheckItem ok={rules.special} text="Un carácter especial (!@#$%^&*)" />
                            </div>
                        )}

                        {/* ------------------------------ */}
                        {/*  CONFIRMAR CONTRASEÑA + OJITO */}
                        {/* ------------------------------ */}
                        <div style={{ position: "relative" }}>
                            <FormField
                                label="Confirmar contraseña"
                                name="repeatPassword"
                                type={showRepeatPwd ? "text" : "password"}
                                value={repeatPassword}
                                onChange={(e) => setRepeatPassword(e.target.value)}
                                required
                                placeholder="Confirmar contraseña"
                                disabled={submitting}
                            />

                            <div
                                onClick={() => setShowRepeatPwd(!showRepeatPwd)}
                                style={{
                                    position: "absolute",
                                    right: 10,
                                    top: 38,
                                    cursor: "pointer",
                                    color: "#666"
                                }}
                            >
                                {showRepeatPwd ? <EyeOff size={20} /> : <Eye size={20} />}
                            </div>
                        </div>

                        <Button type="submit" variant="primary" size="medium" disabled={submitting}>
                            {submitting ? "ACTUALIZANDO DATOS..." : "Enviar"}
                        </Button>
                    </form>

                    {submitting && (
                        <div className={styles.submittingOverlay}>
                            <Loader />
                            <p>Verificando credenciales...</p>
                        </div>
                    )}
                </Box>
            </div>
        </div>
    );
}

function CheckItem({ ok, text }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {ok ? <Check size={14} color="#2ecc71" /> : <X size={14} color="#e74c3c" />}
            <span style={{ color: ok ? "#2ecc71" : "#444" }}>{text}</span>
        </div>
    );
}